import { useAuth0User } from '@/app/modules/Context/useAuth0User';
import { appClientConfig } from '@/app/utils/appClientConfig';
import { getUtmShareUrl } from '@/app/utils/urlHelper';
import { ArticleTags } from '@/cmsComponents/ArticleTags';
import { Author } from '@/cmsComponents/Author';
import { CMSChildrenContainer } from '@/cmsComponents/ChildrenContainer';
import CmsImage from '@/cmsComponents/CmsImage';
import { RelatedArticles } from '@/cmsComponents/RelatedArticles';
import { BackgroundBannerImage } from '@/components/BackgroundBannerImage/BackgroundBannerImage';
import { defaultAlt } from '@/components/Image';
import ShareContainer from '@/components/ShareContainer';
import { PATH } from '@/constants/path';
import ArticleContentWithCta, {
  articleContentHasCtaInsert,
} from '@/modules/ArticleContentWithCta/ArticleContentWithCta';
import { ContentHubContainer } from '@/modules/ContentHubContainer';
import {
  getArticleCategoriesInMenu,
  getArticleDetail,
  getRelatedArticles,
} from '@/services/cmsService';
import { IArticle, IArticleCategorySummary, IArticleSummary } from '@/types/cms';
import { ArticleCtaPositionValues } from '@/types/featureFlags';
import { User } from '@/types/user';
import { getArticleCtaPositionValue } from '@/utils/getServerFeatureFlag';
import { useAnalytics } from '@/utils/useAnalytics';
import { GetServerSidePropsContext } from 'next';
import { ArticleJsonLd, BreadcrumbJsonLd, NextSeo } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { GetServerSidePropsResult } from 'next/types';
import React, { useEffect } from 'react';

import Typography from '@material-ui/core/Typography';

import styles from './style.module.scss';

interface PostProps {
  article: IArticle;
  relatedArticles: IArticleSummary[];
  categories: IArticleCategorySummary[];
  articleCtaPosition: ArticleCtaPositionValues;
}

type ArticleProps = {
  user: User | null;
  article: IArticle;
  relatedArticles: IArticleSummary[];
  articleCtaPosition: ArticleCtaPositionValues;
};

export function Article({ user, article, relatedArticles, articleCtaPosition }: ArticleProps) {
  const {
    article_category: articleCategory,
    author,
    cover,
    article_cta: articleCta,
    title,
    content,
    published_date,
    tags,
  } = article;
  const newLineEncoding = '%0d%0a';

  return (
    <main className={styles.container}>
      <div className={styles.path}>
        <Link prefetch={false} href={PATH.ARTICLES} as={PATH.ARTICLES}>
          <a href={PATH.ARTICLES}>Articles</a>
        </Link>{' '}
        /{' '}
        <Link
          prefetch={false}
          href={`${PATH.ARTICLE_CATEGORY}/${articleCategory?.slug}`}
          as={`${PATH.ARTICLE_CATEGORY}/${articleCategory?.slug}`}
        >
          <a href={`${PATH.ARTICLE_CATEGORY}/${articleCategory?.slug}`}>{articleCategory?.name}</a>
        </Link>
      </div>
      <article>
        <div className={styles.title}>
          <Typography variant="h4" component="h1">
            {title}
          </Typography>
        </div>
        <div>
          {cover && (
            <CmsImage
              imageProps={cover}
              altText={cover.alternativeText ?? defaultAlt}
              format="large"
              priority
              layout="responsive"
              height={cover.height}
              width={cover.width}
              objectFit="cover"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, (max-width: 1920px) 70vw, (max-width: 2560px) 70vw, 1920px"
            />
          )}
          <span>{cover?.caption ?? ''}</span>
        </div>
        <div>
          <ArticleContentWithCta
            content={content}
            articleCta={articleCta}
            ctaPosition={articleCtaPosition}
          />
        </div>
        <div className={styles.authorAndShare}>
          {author && (
            <div className={styles.author}>
              <Author author={author} date={published_date} />
            </div>
          )}
          <ShareContainer
            id={article.id}
            className={styles.share}
            jobOrArticleShare="articleshare"
            emailTitle={
              <>
                Who would you like to <br /> send this article to?
              </>
            }
            emailSubject={`${user?.given_name ?? 'Medrecruit'} shared an article with you`}
            emailBody={`Read the article from ${
              user?.given_name ?? 'Medrecruit'
            } ${newLineEncoding}${encodeURI(
              getUtmShareUrl('articleshare', globalThis.location?.href, 'email'),
            )}`}
            facebookTitle={title}
            shareTrackingProps={{
              name: 'Share Article',
              details: {
                'Share Origin': 'Articles',
                'Article Id': article.id,
                'Article Title': title,
              },
            }}
            popperPlacement={'bottom-end'}
          />
        </div>
      </article>
      <div className={styles.tag}>
        <ArticleTags tags={tags} />
      </div>
      {relatedArticles.length > 0 && (
        <aside>
          <span className={styles.relatedArticleTitle}>Related Articles</span>
          <hr className={styles.line} />
          <div className={styles.relatedArticles}>
            <BackgroundBannerImage priority={false} />
            <CMSChildrenContainer className={styles.articles}>
              <RelatedArticles article={article} articles={relatedArticles} />
            </CMSChildrenContainer>
          </div>
        </aside>
      )}
    </main>
  );
}

const Post = ({ article, relatedArticles, categories, articleCtaPosition }: PostProps) => {
  const track = useAnalytics();
  const router = useRouter();
  const { user } = useAuth0User();

  useEffect(() => {
    track('Article page loaded', {
      title: article.title,
      id: article.id,
      ctaPosition: articleCtaPosition,
    });
  }, [router.query]);

  return (
    <ContentHubContainer
      categories={categories}
      selectedCategorySlug={article.article_category?.slug}
    >
      <NextSeo
        title={article.meta_title}
        description={article.meta_description}
        noindex={!article.index}
        nofollow={!article.index}
        openGraph={{
          title: article.title,
          description: article.blurb,
          type: 'article',
          article: {
            publishedTime: article.published_date,
            authors: [article.author?.name ?? ''],
            tags: article.tags && article.tags.map(tag => tag.name),
          },
          images: [
            {
              url: article.cover?.url ?? '',
              alt: article.cover?.alternativeText ?? defaultAlt,
            },
          ],
        }}
      />
      {/*
        Current issue when in title and description that it does not get parse correctly.
        https://github.com/garmeeh/next-seo/issues/962
      */}
      <ArticleJsonLd
        url={`${appClientConfig.recruitWebUrl}articles/${article.slug}`}
        title={JSON.stringify(article.title).replace(/(^"|"$)/g, '')}
        images={[article.cover?.url ?? '']}
        datePublished={article.published_date}
        dateModified={article.published_date}
        authorName={article.author?.name ?? ''}
        description={JSON.stringify(article.blurb).replace(/(^"|"$)/g, '')}
        publisherName="Medrecruit"
        publisherLogo={`${appClientConfig.recruitWebUrl}logo2x.png`}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Articles',
            item: PATH.ARTICLES,
          },
          {
            position: 2,
            name: 'Category',
            item: `${PATH.ARTICLE_CATEGORY}/${article.article_category?.slug}`,
          },
        ]}
      />

      <Article
        article={article}
        user={user}
        relatedArticles={relatedArticles}
        articleCtaPosition={articleCtaPosition}
      />
    </ContentHubContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<PostProps>> {
  const { query } = ctx;
  const { slug } = query;

  const [article, categoriesOnMenu, articleCtaPositionValue] = await Promise.all([
    getArticleDetail(slug as string),
    getArticleCategoriesInMenu(),
    getArticleCtaPositionValue(ctx),
  ]);

  if (!article) {
    return { notFound: true };
  }

  if (article.redirect_url) {
    return {
      redirect: {
        destination: article.redirect_url,
        permanent: true,
      },
    };
  }

  let relatedArticles: IArticleSummary[] = [];
  if (article.article_category) {
    relatedArticles = await getRelatedArticles(article);
  }

  let articleCtaPosition = articleCtaPositionValue;
  // If there's no INSERT_CTA_HERE value in the content, then the CTA will always be placed
  // at the bottom.
  // We need to default this back to BOTTOM here so that the Mixpanel tracking gets the
  // correct value for where the CTA will be placed.
  if (articleCtaPosition === 'MIDDLE' && !articleContentHasCtaInsert(article.content)) {
    articleCtaPosition = 'BOTTOM';
  }

  return {
    props: {
      article,
      categories: categoriesOnMenu,
      relatedArticles,
      articleCtaPosition,
    },
  };
}

export default Post;
